import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { createSelector, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, take, takeUntil, tap } from 'rxjs/operators';

import {
  addVehicleBindQuery,
  vehicleCAPartnerUse,
  vehicleOwnershipDate,
  vehiclePartnerUse,
  vehiclePrimaryUse
} from '@amfam/policy/add-vehicle/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors, Partners } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';

@Component({
  selector: 'ds-vehicle-use-mileage',
  templateUrl: './vehicle-use-mileage.component.html',
  styleUrls: ['../add-vehicle-container/add-vehicle-container.component.scss'],
  standalone: false
})
export class VehicleUseMileageComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();

  @Output() errorEvent = new EventEmitter<{ hasError: boolean }>();

  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);

  public vehicleUseForm: UntypedFormGroup;
  public isOwnership = false;
  public isOSIPartner = true;
  public newVehicleUsageeHeader: string;
  public vehiclePrimaryUse = <Dictionary<string>>(
    this.copyService.getCopy('policy.vehiclePrimaryUse')
  );

  public primaryUseOptions = [];
  public primaryOwnershipDateOptions = [];
  public vehicleMake = [];

  public isPartnerSpecificEligibleStates = false;
  stateCode: string;
  isPartner = false;

  private vehiclePrimaryUseKeys = Object.values(vehiclePrimaryUse);

  private vehiclePartnerUseKeys = Object.values(vehiclePartnerUse);

  private vehicleOwnershipKeys = Object.values(vehicleOwnershipDate);

  private vehicleCAPartnerUseKeys = Object.values(vehicleCAPartnerUse);
  private osiPartnerCode: Subscription;

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService
  ) {}

  ngOnInit(): void {
    this.initialize();
  }

  initialize() {
    this.osiPartnerCode = this.store.select(BrandSelectors.selectBrandState).subscribe(data => {
      if (data.partnerId === Partners.ONSTAR) {
        this.isOSIPartner = true;
      } else {
        this.isOSIPartner = false;
      }
    });
    this.vehicleMake = this.copyService.getCopy('policy.vehicleMake');
    this.newVehicleUsageeHeader = this.copyService.getCopy('policy.newVehicleUsageHeader');
    this.store.select(vehicleQuery.selectNewVehicle).subscribe(vehicleDetails => {
      this.isOwnership =
        vehicleDetails.year >= 2015 + '' && this.vehicleMake.includes(vehicleDetails.make);
    });

    this.isPartner$
      .pipe(
        tap(isPartner => {
          this.isPartner = isPartner;
          this.loadPrimaryUseOptions();
          this.spinnerService.stop();
          this.buildForm();
          this.handleError();
        })
      )
      .subscribe();
  }

  loadPrimaryUseOptions() {
    this.store
      .select(
        createSelector(
          userQuery.selectUserStateCode,
          BrandSelectors.selectIsPartner,
          (userStateCode: string, isPartner) => ({
            userStateCode,
            isPartner
          })
        )
      )
      .pipe(take(1))
      .subscribe(state => {
        this.stateCode = state.userStateCode;
        this.isPartnerSpecificEligibleStates =
          state.isPartner &&
          this.copyService
            .getCopy('policy.alternativeVehicleUseStates')
            .find(stateName => stateName === state.userStateCode);

        if (this.isPartnerSpecificEligibleStates) {
          this.primaryUseOptions =
            state.userStateCode === 'CA'
              ? this.vehicleCAPartnerUseKeys
              : this.vehiclePartnerUseKeys;
        } else {
          if (state.isPartner) {
            this.vehiclePrimaryUseKeys = this.vehiclePrimaryUseKeys.filter(
              key => key !== vehiclePrimaryUse.antique
            );
          }
          this.primaryUseOptions = this.vehiclePrimaryUseKeys;
        }
        this.primaryOwnershipDateOptions = this.vehicleOwnershipKeys;
      });
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'MainUseMileage' }
      })
    );
  }

  buildForm() {
    const vehicleUsageProps: unknown[] = ['unset'];
    const vehicleOwnerDateProps: unknown[] = ['unset'];
    if (this.isPartner || this.isOSIPartner)
      vehicleUsageProps.push(ValidationService.unsetStringValidator);
    if (this.isOwnership) vehicleOwnerDateProps.push(ValidationService.unsetStringValidator);
    this.vehicleUseForm = this.formBuilder.group({
      mileage: [
        '12,000',
        Validators.compose([
          ValidationService.annualMileageRequiredValidator,
          ValidationService.validateOdometer
        ])
      ],
      ownershipDate: vehicleOwnerDateProps
    });

    this.vehicleUseForm
      .get('mileage')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(val => {
        if (val && val.length > 3) {
          if (val.indexOf(',') > -1) val = val.split(',').join('');
          this.vehicleUseForm.get('mileage').setValue(Number(val).toLocaleString());
        }
      });
  }

  vehicleUseInfoFormSubmit() {
    this.spinnerService.start({
      blockActions: true
    });

    let mileage: string = this.vehicleUseForm.controls.mileage.value || '0';
    mileage = mileage.split(',').join('');

    this.store.dispatch(
      VehicleInfoActions.setVehicleUsageInfo({
        vehicleUsage: {
          primaryUse: null,
          mileage,
          ownershipDateVal: this.vehicleUseForm.controls.ownershipDate.value,
          ownershipDateKey: this.getSelectedOwnershipDateKey()
        }
      })
    );

    this.store.dispatch(
      fromRouterActions.Go({
        path: ['policies/add-vehicle/garage-address']
      })
    );
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    if (this.osiPartnerCode) {
      this.osiPartnerCode.unsubscribe();
    }
  }

  private getSelectedOwnershipDateKey() {
    return <vehicleOwnershipDate>(
      Object.keys(vehicleOwnershipDate).find(
        key => vehicleOwnershipDate[key] === this.vehicleUseForm.controls.ownershipDate.value
      )
    );
  }
  private getSelectedPrimaryUse() {
    if (this.isPartnerSpecificEligibleStates) {
      return this.stateCode === 'CA'
        ? <vehicleCAPartnerUse>(
            Object.keys(vehicleCAPartnerUse).find(
              key => vehicleCAPartnerUse[key] === this.vehicleUseForm.controls.vehicleUsage.value
            )
          )
        : <vehiclePartnerUse>(
            Object.keys(vehiclePartnerUse).find(
              key => vehiclePartnerUse[key] === this.vehicleUseForm.controls.vehicleUsage.value
            )
          );
    }
    return <vehiclePrimaryUse>(
      Object.keys(vehiclePrimaryUse).find(
        key => vehiclePrimaryUse[key] === this.vehicleUseForm.controls.vehicleUsage.value
      )
    );
  }

  private handleError() {
    this.store
      .select(addVehicleBindQuery.selectVehicleANSNotificationError)
      .pipe(takeUntil(this.stop$))
      .subscribe(agentNotificationError => {
        if (agentNotificationError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else {
          this.errorEvent.emit({ hasError: false });
        }
      });
  }
}
